import React from 'react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import SEO from '../components/SEO';
import { Shield, FileText, RefreshCcw } from 'lucide-react';

const LegalPage = ({ type }) => {
  const { settings } = useSiteSettings();
  const legalContent = settings?.legal || {};
  
  const pageConfig = {
    privacy: {
      title: 'Privacy Policy',
      icon: Shield,
      content: legalContent.privacy_policy || defaultPrivacyPolicy,
      seoTitle: 'Privacy Policy | Sentora Holidays',
      seoDescription: 'Read our privacy policy to understand how we collect, use, and protect your personal information.'
    },
    terms: {
      title: 'Terms & Conditions',
      icon: FileText,
      content: legalContent.terms_conditions || defaultTermsConditions,
      seoTitle: 'Terms & Conditions | Sentora Holidays',
      seoDescription: 'Review our terms and conditions for using Sentora Holidays services.'
    },
    refund: {
      title: 'Refund & Cancellation Policy',
      icon: RefreshCcw,
      content: legalContent.refund_policy || defaultRefundPolicy,
      seoTitle: 'Refund Policy | Sentora Holidays',
      seoDescription: 'Understand our refund and cancellation policy for tour bookings.'
    }
  };

  const config = pageConfig[type] || pageConfig.privacy;
  const Icon = config.icon;

  return (
    <div className="bg-s-bg min-h-screen">
      <SEO title={config.seoTitle} description={config.seoDescription} />
      
      {/* Hero */}
      <section className="bg-gradient-to-b from-s-primary/10 to-s-bg pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-s-primary/10 rounded-full flex items-center justify-center">
            <Icon className="w-8 h-8 text-s-primary" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-s-text font-heritage">{config.title}</h1>
          <p className="text-s-muted mt-2">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-s-card border border-s-border rounded-2xl p-6 md:p-10">
          <div 
            className="prose prose-invert max-w-none legal-content"
            dangerouslySetInnerHTML={{ __html: formatContent(config.content) }}
          />
        </div>
      </section>
    </div>
  );
};

// Format content with basic HTML
const formatContent = (content) => {
  if (!content) return '';
  return content
    .split('\n\n')
    .map(para => {
      if (para.startsWith('# ')) {
        return `<h2 class="text-xl font-bold text-s-text mt-6 mb-3">${para.slice(2)}</h2>`;
      }
      if (para.startsWith('## ')) {
        return `<h3 class="text-lg font-semibold text-s-text mt-4 mb-2">${para.slice(3)}</h3>`;
      }
      if (para.startsWith('- ')) {
        const items = para.split('\n').map(item => `<li class="text-s-text2">${item.slice(2)}</li>`).join('');
        return `<ul class="list-disc list-inside space-y-1 my-3">${items}</ul>`;
      }
      return `<p class="text-s-text2 mb-4 leading-relaxed">${para}</p>`;
    })
    .join('');
};

const defaultPrivacyPolicy = `# Privacy Policy

Welcome to Sentora Holidays. We respect your privacy and are committed to protecting your personal data.

## Information We Collect

- Personal identification information (Name, email address, phone number)
- Travel preferences and booking details
- Payment information (processed securely through our payment partners)
- Device and usage information when you visit our website

## How We Use Your Information

We use the information we collect to:
- Process your travel bookings and enquiries
- Communicate with you about your trips
- Send you promotional offers (with your consent)
- Improve our services and website experience

## Data Security

We implement appropriate security measures to protect your personal information against unauthorized access, alteration, or destruction.

## Contact Us

For any privacy-related queries, please contact us at tours@sentoraholidays.com`;

const defaultTermsConditions = `# Terms & Conditions

By using Sentora Holidays services, you agree to these terms and conditions.

## Booking Terms

- All bookings are subject to availability
- A deposit may be required to confirm your booking
- Full payment is typically required 30 days before travel
- Prices are subject to change until booking is confirmed

## Traveler Responsibilities

- Ensure all travel documents (passport, visa, etc.) are valid
- Arrive at designated meeting points on time
- Follow instructions from tour guides and local authorities
- Respect local customs and regulations

## Liability

Sentora Holidays acts as an agent for various service providers. We are not liable for:
- Acts of third-party service providers
- Natural disasters or force majeure events
- Personal injury or loss of belongings
- Flight delays or cancellations

## Modifications

We reserve the right to modify itineraries due to unforeseen circumstances while ensuring equivalent alternatives.

## Contact

For questions about these terms, email us at tours@sentoraholidays.com`;

const defaultRefundPolicy = `# Refund & Cancellation Policy

We understand that plans can change. Here's our refund and cancellation policy.

## Cancellation by Traveler

- More than 30 days before travel: Full refund minus processing fee
- 15-30 days before travel: 50% refund
- 7-14 days before travel: 25% refund
- Less than 7 days before travel: No refund

## Cancellation by Sentora Holidays

If we need to cancel your trip due to unforeseen circumstances:
- Full refund of all payments made
- Option to reschedule to alternative dates
- Credit voucher for future travel

## Non-Refundable Items

- Visa fees and processing charges
- Travel insurance premiums
- Special event tickets (where specified)

## Refund Process

- Refunds are processed within 7-14 business days
- Refunds are credited to the original payment method
- Processing fees may apply

## Contact for Refunds

To request a cancellation or refund, please email tours@sentoraholidays.com with your booking reference.`;

export default LegalPage;
